import { hooks } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import router from "next/router";
import { useTranslations } from "next-intl";
import { useState } from "react";

import Button from "~/components/common/button";
import Icon from "~/components/common/icon";
import myaccount from "~/components/common/myaccount.module.scss";
import { useAddressRequirements } from "~/hooks/use-address-requirements";
import crossCountryUtils from "~/utils/cross-country-utils";

import AddressForm from "./address-form";
import styles from "./address-list.module.scss";
import DeleteAddressDialog from "./delete-address-dialog";
import EditAddressDialog from "./edit-address-dialog";

export default function AddressList() {
  const t = useTranslations();

  const [isEditAddressOpen, setIsEditAddressOpen] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState<any>();
  const [isDeleteAddressOpen, setIsDeleteAddressOpen] = useState<boolean>(false);
  const [addresToDelete, setAddressToDelete] = useState<number>();

  const currentCountryID = crossCountryUtils.getCurrentCountryCode(router);

  const removeAddressListItem = hooks.useCallService({ service: Core.DeleteAddressBookItem });
  const setDefaultBillingAddress = hooks.useCallService({ service: Core.SetDefaultBillingAddress });
  const setDefaultShippingAddress = hooks.useCallService({ service: Core.SetDefaultShippingAddress });

  const { data, refetch } = useAddressRequirements(currentCountryID).getAddressesList;

  const favouriteShipping = data?.find((el) => el.DefaultShippingAddress)?.Address;

  const favouriteBilling = data?.find((el) => el.DefaultBillingAddress)?.Address;

  const removeAddressHandler = async (data: boolean) => {
    if (data && addresToDelete) {
      const response = await removeAddressListItem({ ID: addresToDelete });
      if (!response?.Error) {
        refetch();
        setIsDeleteAddressOpen(false);
      }
    }
  };

  const setDefaultShippingHandler = async (id: number) => {
    const response = await setDefaultShippingAddress({ AddressBookID: id });
    if (!response?.Error) {
      refetch();
    }
  };

  const setDefaultBillingHandler = async (id: number) => {
    const response = await setDefaultBillingAddress({ AddressBookID: id });
    if (!response?.Error) {
      refetch();
    }
  };

  const editAddressHandler = (id: number) => {
    setIsEditAddressOpen(true);
    const addressToEdit = data?.find((el) => el.ID === id);
    setAddressToEdit(addressToEdit);
  };

  return (
    <>
      <div className={myaccount.myaccountCard}>
        <div className={myaccount.myaccountCardHeader}>
          <h2>{t("generic.myaccount.addresses.saved_addresses")}</h2>
        </div>
        <div className={myaccount.myaccountCardContent}>
          {data?.length === 0 && (
            <p className={myaccount.infoMessage}>{t("generic.myaccount.addresses.no_saved_addresses")}</p>
          )}
          {(favouriteShipping || favouriteBilling) && (
            <div className={styles.favourites}>
              {favouriteShipping && (
                <div className={styles.favouriteCard}>
                  <div className={styles.favouriteCardHeader}>
                    <h3>{t("generic.myaccount.addresses.favourite_shipping")}</h3>
                    <Icon name={"star-full"} aria-hidden width={16} height={16} />
                  </div>
                  <p className={styles.favouriteCardCustomerName}>
                    {favouriteShipping?.FirstName} {favouriteShipping?.LastName}
                  </p>
                  <p>
                    {/* TODO understand the proper address formatting depending on the country */}
                    {favouriteShipping?.Address1} {favouriteShipping?.HouseNumber}, {favouriteShipping?.City}{" "}
                    {favouriteShipping?.ZipCode},{" "}
                    {favouriteShipping.CountryID &&
                      crossCountryUtils.getLocalizedCountryName(favouriteShipping.CountryID, router)}
                    {favouriteShipping?.PhoneNumber && (
                      <span>
                        {" · "}
                        {favouriteShipping.PhoneNumber}
                      </span>
                    )}
                  </p>
                </div>
              )}
              {favouriteBilling && (
                <div className={styles.favouriteCard}>
                  <div className={styles.favouriteCardHeader}>
                    <h3>{t("generic.myaccount.addresses.favourite_billing")}</h3>
                    <Icon name={"star-full"} aria-hidden width={16} height={16} />
                  </div>
                  <p className={styles.favouriteCardCustomerName}>
                    {favouriteBilling?.FirstName} {favouriteBilling?.LastName}
                  </p>
                  <p>
                    {/* TODO understand the proper address formatting depending on the country */}
                    {favouriteBilling?.Address1} {favouriteBilling?.HouseNumber}, {favouriteBilling?.City}{" "}
                    {favouriteBilling?.ZipCode},{" "}
                    {favouriteBilling.CountryID &&
                      crossCountryUtils.getLocalizedCountryName(favouriteBilling.CountryID, router)}
                    {favouriteBilling?.PhoneNumber && (
                      <span>
                        {" · "}
                        {favouriteBilling.PhoneNumber}
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}
          {data && data.length > 0 && (
            <div className={styles.addressesListWrapper}>
              <h3>{t("generic.myaccount.addresses.all_addresses")}:</h3>
              <ul className={styles.addressesList}>
                {data?.map((el) => {
                  const address = el.Address!;

                  return (
                    <li className={styles.addressesListCard} key={el.ID}>
                      <div className={styles.addressesListCardHeader}>
                        <p>
                          {address.FirstName} {address.LastName}
                        </p>
                        {(el.DefaultShippingAddress || el.DefaultBillingAddress) && (
                          <Icon name={"star-full"} aria-hidden width={16} height={16} />
                        )}
                      </div>
                      <p>
                        {address.Address1} {address.HouseNumber}, {address.City} {address.ZipCode},{" "}
                        {address.CountryID && crossCountryUtils.getLocalizedCountryName(address.CountryID, router)}
                        {address?.PhoneNumber && (
                          <span>
                            {" · "}
                            {address.PhoneNumber}
                          </span>
                        )}
                      </p>
                      <Button lookAsLink onClick={() => editAddressHandler(el.ID!)}>
                        {t("generic.edit")}
                      </Button>
                      {favouriteShipping?.ID != address.ID && favouriteBilling?.ID != address.ID && (
                        <Button
                          lookAsLink
                          onClick={() => {
                            setAddressToDelete(el.ID);
                            setIsDeleteAddressOpen(true);
                          }}
                        >
                          {t("generic.remove")}
                        </Button>
                      )}
                      {!el.DefaultShippingAddress && (
                        <Button lookAsLink onClick={() => setDefaultShippingHandler(el.ID!)}>
                          {t("generic.addresses.setfavouriteshipping")}
                        </Button>
                      )}
                      {!el.DefaultBillingAddress && (
                        <Button lookAsLink onClick={() => setDefaultBillingHandler(el.ID!)}>
                          {t("generic.addresses.setfavouritebilling")}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
      <AddressForm onAfterSubmit={() => refetch()} />
      <EditAddressDialog
        isOpen={isEditAddressOpen}
        onClose={() => setIsEditAddressOpen(false)}
        address={addressToEdit}
        onAfterSubmit={() => {
          setIsEditAddressOpen(false);
          refetch();
        }}
      />
      <DeleteAddressDialog
        isOpen={isDeleteAddressOpen}
        onClose={() => setIsDeleteAddressOpen(false)}
        onSubmit={removeAddressHandler}
      />
    </>
  );
}
